import { useState } from "react";
import { ScrollView, Text, View, Pressable } from "react-native";
import { useIsFocused, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { trpc } from "../../lib/trpc";
import {
  Card,
  Select,
  ErrorState,
  LoadingSpinner,
  Money,
  PageContainer,
  PageHeader,
  TaxClassificationSection,
  WebHead,
} from "../../components";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useColors } from "../../lib/colors";
import { formatCurrency } from "../../lib/utils";

function SectionDivider({ title }: { title: string }) {
  return (
    <View className="flex-row items-center gap-2 mt-1 mb-2">
      <Text className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {title}
      </Text>
      <View className="flex-1 h-px bg-gray-100 dark:bg-gray-700" />
    </View>
  );
}

/** Heuristic: any value that contains "$" is a financial value and renders in DM Mono. */
const isCurrency = (v: string) => v.includes("$");

function Row({
  label,
  value,
  valueColor,
  bold,
  indent,
  muted,
}: {
  label: string;
  value: string;
  valueColor?: string;
  bold?: boolean;
  indent?: boolean;
  muted?: boolean;
}) {
  const valueClass = `text-sm ${bold ? "font-semibold" : ""} ${
    valueColor ?? (muted ? "text-gray-500 dark:text-gray-400" : "text-gray-800 dark:text-gray-200")
  }`;
  return (
    <View className={`flex-row items-center justify-between py-1.5 ${indent ? "pl-3" : ""}`}>
      <Text
        className={`text-sm ${bold ? "font-semibold" : ""} ${
          muted ? "text-gray-500 dark:text-gray-400" : "text-gray-700 dark:text-gray-300"
        }`}
      >
        {label}
      </Text>
      {isCurrency(value) ? (
        <Money weight={bold ? 500 : 400} className={valueClass}>
          {value}
        </Money>
      ) : (
        <Text className={valueClass}>{value}</Text>
      )}
    </View>
  );
}

function TotalRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  const valueClass = `text-sm font-semibold ${valueColor ?? "text-gray-800 dark:text-gray-200"}`;
  return (
    <View className="flex-row items-center justify-between pt-2 mt-1 border-t border-gray-100 dark:border-gray-700">
      <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </Text>
      {isCurrency(value) ? (
        <Money weight={500} className={valueClass}>
          {value}
        </Money>
      ) : (
        <Text className={valueClass}>{value}</Text>
      )}
    </View>
  );
}

function filingStatusLabel(fs: string | null): string {
  if (fs === "married_filing_jointly") return "Married filing jointly";
  if (fs === "single") return "Single";
  return "Not set";
}

function formatTaxCurrency(value: number): string {
  return formatCurrency(value, { decimals: 0 });
}

export default function TaxDashboardScreen() {
  const isFocused = useIsFocused();
  const router = useRouter();
  const c = useColors();
  const isDesktop = useIsDesktop();

  const [year, setYear] = useState(String(Math.min(2027, new Date().getFullYear())));
  const q = trpc.tax.currentYearEstimate.useQuery({ year: Number(year) }, {
    enabled: isFocused,
  });

  if (q.isLoading) return <LoadingSpinner />;
  if (q.error) {
    return (
      <ErrorState
        error={q.error}
        screen="tax-dashboard"
        onRetry={() => q.refetch()}
      />
    );
  }

  const data = q.data!;
  const { income, adjustments, deductions, tax, credits, payments, result } = data;
  const owes = result.refundOrOwe < 0;
  const absResult = Math.abs(result.refundOrOwe);

  return (
    <>
      <WebHead>
        <title>Tax Dashboard – Per Diem</title>
      </WebHead>
      <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer className={isDesktop ? "" : "p-4"}>
          <View className="gap-4">
            <PageHeader
              title={`Tax Dashboard — ${data.year}`}
              subtitle="Full-year projection from saved plans and recorded spending"
              closeTo="/(app)/fire-plan"
            />

            <Select label="Tax year" value={year} onChange={setYear}
              options={[2024, 2025, 2026, 2027].map((value) => ({ value: String(value), label: String(value) }))} />
            <Card>
              <Text className="text-sm text-gray-600 dark:text-gray-300">
                {data.projection.asOf
                  ? `Recorded spending through ${data.projection.asOf}: ${data.projection.elapsedMonths.toFixed(2)} calendar months, projected at ${data.projection.annualizationFactor.toFixed(2)}× YTD. Income and withholding use saved monthly plans over their active dates.`
                  : "Future-year plan: saved monthly income and withholding; no YTD transactions available."}
                {data.year > data.projection.federalTableYear ? ` Provisional federal estimate using ${data.projection.federalTableYear} thresholds.` : ""}
                {" Realized gains include recorded sales only."}
              </Text>
            </Card>

            {/* Result hero */}
            <Card>
              <View className="items-center py-4 gap-1">
                <View className="flex-row items-center gap-2">
                  <Text className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3">
                    {owes ? "You may owe" : "You may get a refund"}
                  </Text>
                  <View className="rounded-full bg-amber-100 px-2 py-0.5 dark:bg-amber-900/40">
                    <Text className="text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 dark:text-amber-300">
                      Beta
                    </Text>
                  </View>
                </View>
                <Money
                  weight={500}
                  className={`text-[32px] leading-[1.1] mt-1 ${
                    owes ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-green-500"
                  }`}
                >
                  {formatTaxCurrency(absResult)}
                </Money>
                <Money className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  {formatTaxCurrency(result.totalPayments)} projected payments vs{" "}
                  {formatTaxCurrency(result.totalTaxAfterCredits)} estimated tax
                </Money>
              </View>
            </Card>

            {/* Filing */}
            <Card>
              <SectionDivider title="Filing" />
              <Row label="Filing status" value={filingStatusLabel(data.filingStatus)} />
              <Row label="State" value={data.state ?? "Not set"} />
              {data.locality && <Row label="Locality" value={data.locality} />}
              <Row label="Dependents" value={String(data.dependents)} />
            </Card>

            {/* Income */}
            <Card>
              <SectionDivider title="Income" />
              {income.wages > 0 && <Row label="Wages" value={formatTaxCurrency(income.wages)} />}
              {income.passive > 0 && (
                <Row label="Passive income" value={formatTaxCurrency(income.passive)} />
              )}
              {income.shortTermGains !== 0 && (
                <Row label="Short-term capital gains" value={formatTaxCurrency(income.shortTermGains)} />
              )}
              {income.longTermGains !== 0 && (
                <Row label="Long-term capital gains" value={formatTaxCurrency(income.longTermGains)} />
              )}
              {income.socialSecurity > 0 && (
                <>
                  <Row
                    label="Social Security (gross)"
                    value={formatTaxCurrency(income.socialSecurity)}
                  />
                  <Row
                    label="Taxable portion"
                    value={formatTaxCurrency(income.taxableSocialSecurity)}
                    indent
                    muted
                  />
                </>
              )}
              <TotalRow label="Total gross" value={formatTaxCurrency(income.totalGross)} />
            </Card>

            {/* Adjustments / AGI */}
            <Card>
              <SectionDivider title="Adjustments" />
              {adjustments.pretax401k > 0 && (
                <Row
                  label="Pre-tax 401(k)"
                  value={`− ${formatTaxCurrency(adjustments.pretax401k)}`}
                  valueColor="text-forest-600 dark:text-green-500"
                />
              )}
              {adjustments.hsaContribution > 0 && (
                <Row
                  label="HSA"
                  value={`− ${formatTaxCurrency(adjustments.hsaContribution)}`}
                  valueColor="text-forest-600 dark:text-green-500"
                />
              )}
              {adjustments.total === 0 && (
                <Text className="text-xs text-gray-400 dark:text-gray-500 py-1">
                  No pre-tax adjustments
                </Text>
              )}
              <TotalRow label="AGI" value={formatTaxCurrency(data.agi)} />
            </Card>

            {/* Deductions */}
            <Card>
              <SectionDivider title="Deductions" />
              <Row
                label={`Standard${deductions.using === "standard" ? " (using)" : ""}`}
                value={formatTaxCurrency(deductions.standardDeduction)}
                bold={deductions.using === "standard"}
              />
              {deductions.nonitemizedCharitable > 0 && <Row label="Cash gifts (nonitemizer)" value={formatTaxCurrency(deductions.nonitemizedCharitable)} />}
              <View className="mt-2">
                <Text className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                  Itemized{deductions.using === "itemized" ? " (using)" : " (preview)"}
                </Text>
                {deductions.itemized.saltCapped > 0 && (
                  <>
                    <Row
                      label="State income tax"
                      value={formatTaxCurrency(deductions.itemized.saltStateIncomeTax)}
                      indent
                      muted
                    />
                    <Row
                      label="Property tax"
                      value={formatTaxCurrency(deductions.itemized.saltPropertyTax)}
                      indent
                      muted
                    />
                    <Row
                      label={`SALT (limit ${formatTaxCurrency(deductions.itemized.saltLimit)})`}
                      value={formatTaxCurrency(deductions.itemized.saltCapped)}
                      indent
                    />
                  </>
                )}
                {deductions.itemized.mortgageInterest > 0 && (
                  <Row
                    label="Mortgage interest"
                    value={formatTaxCurrency(deductions.itemized.mortgageInterest)}
                    indent
                  />
                )}
                {deductions.itemized.charitable > 0 && (
                  <Row
                    label="Charitable"
                    value={formatTaxCurrency(deductions.itemized.charitable)}
                    indent
                  />
                )}
                {deductions.itemized.medical > 0 && (
                  <>
                    <Row
                      label="Medical (gross)"
                      value={formatTaxCurrency(deductions.itemized.medical)}
                      indent
                      muted
                    />
                    <Row
                      label="Less 7.5% AGI floor"
                      value={`− ${formatTaxCurrency(deductions.itemized.medicalFloor)}`}
                      indent
                      muted
                    />
                  </>
                )}
                <Row
                  label="Itemized total"
                  value={formatTaxCurrency(deductions.itemized.total)}
                  indent
                  bold={deductions.using === "itemized"}
                />
              </View>
              <TotalRow
                label={`Using ${deductions.using}`}
                value={formatTaxCurrency(deductions.chosenAmount)}
              />
            </Card>

            {/* Taxable income + Tax */}
            <Card>
              <SectionDivider title="Tax" />
              <Row label="Taxable income" value={formatTaxCurrency(data.taxableIncome)} />
              <Row label="Federal" value={formatTaxCurrency(tax.federal)} />
              {tax.federalLtcg > 0 && <Row label="Federal capital-gains portion" value={formatTaxCurrency(tax.federalLtcg)} indent muted />}
              {tax.niit > 0 && <Row label="Net investment income tax (included)" value={formatTaxCurrency(tax.niit)} indent muted />}
              {tax.state > 0 && <Row label="State" value={formatTaxCurrency(tax.state)} />}
              {tax.local > 0 && <Row label="Local" value={formatTaxCurrency(tax.local)} />}
              <TotalRow label="Total tax" value={formatTaxCurrency(tax.total)} />
              <Text className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Effective rate: {(tax.effectiveRate * 100).toFixed(1)}%
              </Text>
            </Card>

            {/* Credits */}
            {credits.total > 0 && (
              <Card>
                <SectionDivider title="Credits" />
                {credits.ctc > 0 && (
                  <Row
                    label="Child Tax Credit"
                    value={`− ${formatTaxCurrency(credits.ctc)}`}
                    valueColor="text-forest-600 dark:text-green-500"
                  />
                )}
                <TotalRow
                  label="Total credits"
                  value={`− ${formatTaxCurrency(credits.total)}`}
                  valueColor="text-forest-600 dark:text-green-500"
                />
              </Card>
            )}

            {/* Payments */}
            <Card>
              <SectionDivider title="Withholding & Payments" />
              {payments.federalWithholdingProjected > 0 && (
                <>
                  <Row
                    label="Federal withholding (modeled YTD)"
                    value={formatTaxCurrency(payments.federalWithholdingYtd)}
                    muted
                  />
                  <Row
                    label="Federal withholding (projected)"
                    value={formatTaxCurrency(payments.federalWithholdingProjected)}
                  />
                </>
              )}
              {payments.stateWithholdingProjected > 0 && (
                <Row
                  label="State withholding (projected)"
                  value={formatTaxCurrency(payments.stateWithholdingProjected)}
                />
              )}
              {payments.estimatedTaxPaymentsProjected > 0 && (
                <>
                  <Row
                    label="Estimated payments"
                    value={formatTaxCurrency(payments.estimatedTaxPaymentsProjected)}
                  />
                  {payments.estimatedFromClassified > 0 && (
                    <Row
                      label="From bank transactions"
                      value={formatTaxCurrency(payments.estimatedFromClassified)}
                      indent
                      muted
                    />
                  )}
                  {payments.estimatedFromDeclared > 0 && (
                    <Row
                      label="Declared"
                      value={formatTaxCurrency(payments.estimatedFromDeclared)}
                      indent
                      muted
                    />
                  )}
                </>
              )}
              {payments.fica > 0 && (
                <Row
                  label="FICA (info only)"
                  value={formatTaxCurrency(payments.fica)}
                  muted
                />
              )}
              <TotalRow label="Total payments" value={formatTaxCurrency(payments.totalProjected)} />
            </Card>

            {/* Result breakdown */}
            <Card>
              <SectionDivider title="Result" />
              <Row
                label="Total tax after credits"
                value={formatTaxCurrency(result.totalTaxAfterCredits)}
              />
              <Row label="Total payments" value={formatTaxCurrency(result.totalPayments)} />
              <TotalRow
                label={owes ? "You may owe" : "You may get a refund"}
                value={formatTaxCurrency(absResult)}
                valueColor={
                  owes ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-green-500"
                }
              />
            </Card>

            {/* Classifications / declared payments */}
            <Card>
              <SectionDivider title="Tax Payments" />
              <TaxClassificationSection
                currentYear={data.year}
                uncategorized={data.uncategorizedTaxTransactions}
                classified={data.taxTransactions}
              />
            </Card>

            {/* Warnings */}
            {data.warnings.length > 0 && (
              <Card>
                <SectionDivider title="Notes" />
                {data.warnings.map((w, i) => (
                  <View key={i} className="flex-row gap-2 py-1">
                    <Ionicons name="information-circle-outline" size={14} color={c.textSecondary} />
                    <Text className="flex-1 text-xs text-gray-500 dark:text-gray-400">{w}</Text>
                  </View>
                ))}
              </Card>
            )}

            {/* Link to Tax Strategy */}
            <Pressable
              onPress={() => router.push("/tax-strategy" as any)}
              className="flex-row items-center justify-center gap-1.5 py-3"
            >
              <Ionicons name="options-outline" size={16} color={c.primary} />
              <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">
                View Tax Strategy
              </Text>
              <Ionicons name="chevron-forward" size={14} color={c.primary} />
            </Pressable>
          </View>
        </PageContainer>
      </ScrollView>
    </>
  );
}
